import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function VerifyOtp() {
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { verifySignUpOtp, resendSignUpOtp, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get email from URL params or localStorage
    const emailFromParams = searchParams.get('email');
    const emailFromStorage = localStorage.getItem('signup_email');
    
    if (emailFromParams) {
      setEmail(emailFromParams);
      localStorage.setItem('signup_email', emailFromParams);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    }

    // Start countdown for resend button
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp.trim()) {
      setError('Please enter the OTP code');
      return;
    }

    if (!email) {
      setError('Email not found. Please try signing up again.');
      return;
    }

    try {
      const { error: verifyError } = await verifySignUpOtp(email, otp);
      
      if (verifyError) {
        throw verifyError;
      }

      setSuccess('Email verified successfully! Redirecting to login...');
      localStorage.removeItem('signup_email');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to verify OTP');
    }
  };

  const handleResendOtp = async () => {
    if (!email || countdown > 0) return;

    try {
      setIsResending(true);
      setError('');
      
      const { error: resendError } = await resendSignUpOtp(email);
      
      if (resendError) {
        throw resendError;
      }

      setSuccess('OTP resent successfully! Check your email.');
      setCountdown(60);
      
      // Start countdown again
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  return (
    <div className="pen-screen flex items-center justify-center px-5 py-8">
      <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow">
        <div className="text-center mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center h-10 px-3.5 bg-sw-yellow text-black font-black text-sm tracking-[0.1em] uppercase rounded-sm"
          >
            HYROX
          </Link>
          <p className="mt-3 text-[10px] tracking-[0.14em] uppercase text-sw-muted-2 font-mono font-light">
            Penalty Logger
          </p>
        </div>

        <h1 className="pen-title">Verify Your Email</h1>
        <p className="pen-lede">
          We&apos;ve sent a verification code to{' '}
          <span className="text-white font-extrabold">{email}</span>
        </p>

        {error && <div className="pen-error mb-4">{error}</div>}
        {success && <div className="pen-success mb-4">{success}</div>}

        <form className="space-y-3" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="otp" className="pen-label">
              OTP code
            </label>
            <input
              id="otp"
              name="otp"
              type="text"
              autoComplete="one-time-code"
              required
              className="pen-input font-mono text-lg text-center tracking-[0.3em]"
              placeholder="Enter 6-digit code"
              value={otp}
              onChange={handleOtpChange}
              maxLength={6}
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || !otp.trim()}
            className="pen-btn-primary w-full"
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="text-center space-y-3 mt-5">
          <button
            onClick={handleResendOtp}
            disabled={countdown > 0 || isResending || !email}
            className="pen-link disabled:opacity-50 disabled:no-underline"
          >
            {isResending
              ? 'Sending...'
              : countdown > 0
                ? `Resend in ${countdown}s`
                : 'Resend OTP'}
          </button>

          <p className="text-xs text-sw-muted">
            Didn&apos;t receive the code? Check your spam folder or{' '}
            <button
              onClick={handleResendOtp}
              disabled={countdown > 0 || isResending || !email}
              className="pen-link inline disabled:opacity-50"
            >
              try again
            </button>
          </p>

          <div className="pt-4 border-t border-sw-border">
            <p className="text-xs text-sw-muted">
              Already verified?{' '}
              <Link to="/login" className="pen-link inline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
